import {get, map, find, isString, isEmpty, isNil, isArray,
    assign, pick, omit, forIn, isNumber, isEqual, reduce, sortBy,
    every} from 'lodash';
import {useState, useCallback, useMemo, useRef, useContext, useEffect} from 'react';
import * as tmp from 'react-router-dom';
import {getColumnKey, getColumnPos} from 'acud/lib/table/util';

import useTableFetch from './fetch';
import useCommand from './command';
import {TGetTableOptions, TCloumns} from './interface';

import {useLocale} from '../../../components/locale';
import {ConfigContext} from '../../../components/common/ConfigProvider';
import useSearchParams from '../../common/searchParams';
import Enum from '../../../utils/Enum';
import UploadForm from './Form/UploadForm';
import AttributesForm from './Form/AttributesForm';
import DownloadForm from './Form/DownloadForm';

const DEFAULT_PAGE_NO = 1;
const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGESIZE_OPTIONS = ['10', '20', '50', '100'];
const DEFAULT_PAGE_PARAMS = ['pageNo', 'pageSize'];
const DEFAULT_QUERY_PARAMS = [...DEFAULT_PAGE_PARAMS, 'orderBy', 'order'];

// ignore waring `"export 'useNavigate' (imported as 'rc') was not found in 'react-router-dom'`
const rc = tmp as any;

interface SearchParams {
    pageNo?: number;
    pageSize?: number;
    orderBy?: string;
    order?: string;
}

interface DefaultFilterData {
    order?: string;
    orderBy?: string;
}

const TABLE_ORDER_MAP = new Enum(
    {alias: 'ascend', value: 'asc'},
    {alias: 'descend', value: 'desc'}
);

const getSearch = location => {
    const {hash, search} = location;
    return (hash || search)?.split('?')[1];
};

const getUrl = (location, searchParamsString) => {
    const {pathname, hash} = location;
    let search = `?${searchParamsString}`;
    if (hash) {
        search = hash.indexOf('?') > -1 ? hash.replace(/\?.*/, () => search) : hash + search;
    }
    return pathname + search;
};

let tableData = [];

/**
 * useTable Hook 功能总结:
 *
 * 1. 表格数据管理
 *    - 分页参数管理 (pageNo, pageSize)
 *    - 排序参数管理 (orderBy, order)
 *    - 搜索参数管理
 *    - URL参数同步
 *
 * 2. 表格操作功能
 *    - 行选择管理 (selectedRowKeys, selectedItems)
 *    - 表格命令处理 (handleTableCommand)
 *    - 批量操作支持
 *
 * 3. 弹窗管理
 *    - Dialog/Drawer/Modal 弹窗控制
 *    - 弹窗内容渲染
 *    - 弹窗事件处理
 *
 * 4. 数据获取
 *    - 集成 useTableFetch 进行数据请求
 *    - 错误处理和加载状态
 *    - 数据缓存和刷新
 *
 * 5. 路由集成
 *    - 支持 React Router 历史记录
 *    - URL参数与表格状态同步
 *    - 页面跳转和参数传递
 *
 * 6. 国际化支持
 *    - 多语言文本处理
 *    - 本地化配置
 *
 * 7. 列控制
 *    - 动态列配置
 *    - 列显示/隐藏控制
 *    - 列排序和筛选
 *
 * 8. 自定义功能扩展
 *    - 支持自定义函数注入
 *    - 灵活的配置选项
 *    - 插件化架构
 */

const useTable = (getOptions: TGetTableOptions, props?: any, customFunctions: any = {}) => {

    const {actions, history: propsHistory, search: propsSearch} = props;

    const hooksHistory = rc.useHistory?.();
    const navigate = rc.useNavigate?.();
    const hooksLocation = rc.useLocation?.();

    const history = propsHistory || hooksHistory || navigate;
    const location = history?.location || hooksLocation;
    const search = propsSearch || getSearch(location);

    const {getSearchParam, searchParamsInstance} = useSearchParams(search);

    const {queryParamKeys: tableQueryParamKeys, fetchActionErrorToast} = useContext(ConfigContext)?.table || {};
    const [cloumnKeys, setCloumnKeys] = useState<TCloumns[]>([]);

    const callbackRef = useRef<any>({
        tableActions: {
            getSearchParam
        }
    });

    const [selectedRow, setSelectedRow] = useState({selectedRowKeys: [], selectedItems: []});

    const {dialogProps, handleCommand} = useCommand({actions, history, selectedRow, ...callbackRef.current});

    const [locale] = useLocale('useTable');

    // 表格中事件处理
    const handleTableCommand = useCallback((type: string, payload?: any) => {
        const {tableOptions} = callbackRef.current;
        const commandConfig = get(tableOptions, `commands[${type}]`);
        commandConfig && handleCommand({
            config: commandConfig,
            payload
        });
    }, [handleCommand]);

    const options = getOptions(
        props,
        handleTableCommand,
        {...callbackRef.current.tableActions, ...customFunctions}
    );
    // 多于20个列表设置列, 展示形态
    const isMore20 = options?.tableConfig?.columns?.length > 20 && options?.tableConfig?.isControlColumn;
    const tableOptions = {
        ...options,
        commands: {
            ...(options?.commands || {}),
            ...options?.commands?.UPLOAD ? {
                UPLOAD: {
                    skin: 'enhance',
                    ...options?.commands?.UPLOAD,
                    dialog: {
                        title: '批量导入',
                        width: 540,
                        foot: true,
                        maskClosable: false,
                        destroyOnClose: true,
                        ...(options?.commands?.UPLOAD?.dialog || {}),
                        body: {
                            type: 'common',
                            $payloadFields: ['actions', 'alterMsg', 'typeOptions', 'uploagConfig', 'extraMsg'],
                            content: UploadForm,
                            ...(options?.commands?.UPLOAD?.dialog?.body || {})
                        }
                    }
                }
            } : {},
            ...options?.commands?.EXPORT ? {
                EXPORT: {
                    skin: 'enhance',
                    ...options?.commands?.EXPORT,
                    dialog: {
                        title: '导出数据',
                        width: 580,
                        foot: true,
                        maskClosable: false,
                        destroyOnClose: true,
                        ...(options?.commands?.EXPORT?.dialog || {}),
                        body: {
                            type: 'common',
                            $payloadFields: ['actions', 'fileName'],
                            $extraPayload: {
                                selectMode: options?.select,
                                dataSource: options?.select === 'none' ? tableData : selectedRow?.selectedItems || [],
                                options
                            },
                            content: DownloadForm
                        }
                    }
                }
            } : {},
            ...isMore20 ? {
                ATTRIBUTES: {
                    actionType: 'drawer',
                    drawer: {
                        title: '列表显示属性配置',
                        width: 400,
                        foot: true,
                        maskClosable: false,
                        destroyOnClose: true,
                        body: {
                            content: AttributesForm,
                            $payloadFields: ['cloumnKeys', 'setCloumnKeys']
                        }
                    }
                }
            } : {}
        }
    };

    const {
        className,
        title,
        // 请求相关配置
        fetchListAction,
        $onRequest,
        $onResponse,
        $onError,
        errorToast = fetchActionErrorToast ?? true,
        abort,
        interval,
        queryParamKeys: defaultQueryParamKeys = (tableQueryParamKeys ?? false),
        $onQueryParamsChange,
        controlsConfig,
        // 批量操作相关配置
        bulkConfig,
        bulkComp,
        // bulkPosition,
        // 筛选区域相关配置
        filterConfig: defaultFilterConfig,
        multiFilterConfig,
        filterComp,
        // 表格相关配置
        rowKey,
        select = 'multi',
        pageNo: defaultPageNo,
        pageSize: defaultPageSize,
        pageSizeOptions,
        tableConfig,
        tableComp,
        card,
        emptyConfig,
        setColumnsConfig
        // 表格操作相关配置
        // commands

    } = tableOptions;

    const queryParamKeys = useMemo(() => {
        if (!defaultQueryParamKeys) {
            return [];
        }
        return (defaultQueryParamKeys === true ? DEFAULT_QUERY_PARAMS : defaultQueryParamKeys) as string[];
    }, [defaultQueryParamKeys]);

    const getQueryParamValue = useCallback(paramName => {
        const searchParams = getSearchParam() as SearchParams;
        return queryParamKeys.includes(paramName) ? searchParams?.[paramName] : undefined;
    }, [queryParamKeys, getSearchParam]);

    const getCustomQueryParamsValue = useCallback(() => {
        const searchParams = getSearchParam() as SearchParams;
        return omit(pick(searchParams, queryParamKeys), DEFAULT_PAGE_PARAMS);
    }, [queryParamKeys, getSearchParam]);

    const {columns, cloumnsKeyList, filterConfig, defaultFilterData} = useMemo(() => {
        const defaultFilterData = <DefaultFilterData>{};

        let cloumnsKeyList = isMore20 && cloumnKeys.length !== 0 ? [...cloumnKeys] : [...(tableConfig?.columns || [])];
        const cloumnShowKeys = reduce(cloumnsKeyList, (res, item, index) => {
            if (isMore20 && (item.show || item.isUniq) || !isMore20) {
                res[item.dataIndex] = index + 1;
                res[item.key] = index + 1;
            }
            return res;
        }, {} as {[key: string]: number});
        // 遍历columns：获取默认筛选值、排序；设置默认筛选值、排序
        let columns = reduce(tableConfig?.columns, (res, column, columnIndex: number) => {
            const {
                dataIndex,
                filters, filterDropdown, filteredValue, defaultFilteredValue,
                // filterMultiple = true, // 筛选
                sorter, sortOrder, defaultSortOrder// 排序
            } = column;
            const defaultKey = getColumnPos(columnIndex);
            const columnKey = getColumnKey(column, defaultKey);
            column.sortIndex = cloumnShowKeys[columnKey] || 0;
            // 1. 判断是否有筛选项默认值
            if (filters || filterDropdown) {
                const queryParamValue = getQueryParamValue(columnKey);
                if ('filteredValue' in column || 'defaultFilteredValue' in column) {
                    defaultFilterData[columnKey] = filteredValue ?? defaultFilteredValue;
                }
                else if (queryParamValue) {
                    // const paramValue = filterMultiple && isString(queryParamValue)
                    //     ? [queryParamValue] : queryParamValue;
                    // 对于单选多选都使用数组，不然onTableChange时filter数据会丢失
                    const paramValue = isString(queryParamValue) ? [queryParamValue] : queryParamValue;
                    defaultFilterData[columnKey] = paramValue;
                    column.defaultFilteredValue = paramValue;
                }
            }
            // 2. 判断是否有排序默认值
            if (sorter) {
                const orderParamValue = getQueryParamValue('order');
                const orderByParamValue = getQueryParamValue('orderBy');
                // 与排序不同，这里的key是dataIndex
                if ('sortOrder' in column || 'defaultSortOrder' in column) {
                    const order = sortOrder ?? defaultSortOrder;
                    defaultFilterData.order = TABLE_ORDER_MAP[order];
                    defaultFilterData.orderBy = dataIndex;
                }
                else if (orderByParamValue === dataIndex && orderParamValue) {
                    defaultFilterData.order = orderParamValue;
                    defaultFilterData.orderBy = dataIndex;
                    column.defaultSortOrder = TABLE_ORDER_MAP[orderParamValue];
                }
            }
            if (!isMore20 || cloumnShowKeys[columnKey]) {
                res.push(column);
            }
            return res;
        }, [] as any[]);
        if (isMore20) {
            columns = sortBy(columns, 'sortIndex');
        }
        // 遍历controls：获取默认搜索值等；设置默认搜索值等
        const controls = map(defaultFilterConfig?.controls, control => {
            const {name, controlProps} = control;
            if (!name) {
                return control;
            }
            const {value, checked, defaultValue, defaultChecked} = controlProps || {};
            const queryParamValue = getQueryParamValue(name);
            if (
                'value' in controlProps
                || 'defaultValue' in controlProps
                || 'checked' in controlProps
                || 'defaultChecked' in controlProps
            ) {
                defaultFilterData[name] = value ?? checked ?? defaultValue ?? defaultChecked;
            }
            else if (queryParamValue) {
                defaultFilterData[name] = queryParamValue;
                if (!control.controlProps) {
                    control.controlProps = {};
                }
                control.controlProps.defaultValue = queryParamValue;
                control.controlProps.defaultChecked = queryParamValue === 'true'; // 转成Boolean
            }
            return control;

        });
        return {
            columns,
            cloumnsKeyList,
            filterConfig: {
                ...defaultFilterConfig,
                controls
            },
            defaultFilterData
        };
    }, [tableConfig?.columns, defaultFilterConfig, getQueryParamValue, cloumnKeys, isMore20]);

    const action = useMemo(() => (
        isString(fetchListAction) ? get(actions, fetchListAction) : fetchListAction
    ), [fetchListAction, actions]);

    const defaultListParams = {
        pageNo: Number(getQueryParamValue('pageNo') || defaultPageNo || DEFAULT_PAGE_NO),
        pageSize: Number(getQueryParamValue('pageSize') || defaultPageSize || DEFAULT_PAGE_SIZE),
        // 若url中参数与filter中参数重复，则以filter中参数为准
        ...getCustomQueryParamsValue(),
        ...defaultFilterData
    };

    const {loading, data, reload, listParams, setListParams} = useTableFetch({
        action,
        defaultListParams,
        errorToast,
        $onRequest,
        $onResponse,
        $onError,
        abort,
        interval
    });

    const [filterData, setFilterData] = useState(defaultFilterData);

    const {
        result: dataSource,
        totalCount,
        httpStatusCode
    } = data || {} as any;

    tableData = dataSource;
    const pageNo = (data as any)?.pageNo || listParams?.pageNo;
    const pageSize = (data as any)?.pageSize || listParams?.pageSize;

    const loadList = useCallback(() => {
        // 清空筛选
        setSelectedRow({selectedRowKeys: [], selectedItems: []});
        reload();
    }, [reload]);

    const handleQueryParamsChange = useCallback(() => {
        const searchParamsString = searchParamsInstance.toString();
        if ($onQueryParamsChange) {
            const searchParams = getSearchParam() as Record<string, string>;
            return $onQueryParamsChange(searchParams, searchParamsString);
        }
        const url = getUrl(location, searchParamsString);
        history.push ? history.push(url) : history(url);
    }, [history, location, searchParamsInstance, getSearchParam, $onQueryParamsChange]);

    const updateQueryParams = useCallback(queryParams => {
        if (isEmpty(queryParamKeys)) {
            return;
        }
        forIn(queryParams, (paramValue, paramKey) => {
            if (!queryParamKeys.includes(paramKey)) {
                return;
            }
            switch (true) {
                case isNumber(paramValue):
                case isString(paramValue):
                    searchParamsInstance.set(paramKey, `${paramValue}`);
                    break;
                case isArray(paramValue):
                    searchParamsInstance.delete(paramKey);
                    paramValue.map(paramValueItem => {
                        searchParamsInstance.append(paramKey, paramValueItem);
                    });
                    break;
                case isNil(paramValue):
                    searchParamsInstance.delete(paramKey);
                    break;
                default:
                    break;
            }
        });
        handleQueryParamsChange();
    }, [queryParamKeys, searchParamsInstance, handleQueryParamsChange]);

    const updateListParams = useCallback(params => {
        const multiply = params?.multiply;
        if (multiply === true) {
            delete params?.multiply;
        }
        let validParams = {...params};
        let unValidKeys = [] as any[];
        if (multiply === true) {
            validParams = reduce(params, (acc, item: {searchParam: string, value: any}) => {
                if (item.searchParam && item.value) {
                    acc[item.searchParam] = item.value;
                }
                else {
                    unValidKeys.push(item.searchParam);
                }
                return acc;
            }, {});
        }
        setSelectedRow({selectedRowKeys: [], selectedItems: []});
        setListParams((prevParams: any) => {
            let validPrevParams = {...prevParams};
            if (multiply === true) {
                validPrevParams = reduce(prevParams, (acc, value: any, key: string) => {
                    if (!unValidKeys.includes(key)) {
                        acc[key] = value;
                    }
                    return acc;
                }, {});
            }
            const newParams = {...validPrevParams, ...validParams};
            updateQueryParams({...newParams});
            return newParams;
        });
    }, [setListParams, updateQueryParams]);

    const updateFilterData = useCallback(params => {
        setFilterData((prevFilterData: any) => ({...prevFilterData, ...params}));
    }, []);

    const loadFirstPage = useCallback(() => {
        pageNo !== 1 ? updateListParams({pageNo: 1}) : loadList();
    }, [pageNo, updateListParams, loadList]);

    const handleTableChange = useCallback((pagination, filters, sorter) => {
        const {current: nextCurrent, pageSize: nextPageSize} = pagination;
        const isPaginationChange = nextCurrent !== pageNo || nextPageSize !== pageSize;
        // 这里处理 filters 和 sorter 的变化
        const {order, field} = sorter;
        const filterData = {
            ...(order ? {order: TABLE_ORDER_MAP[order], orderBy: field} : {}),
            ...filters
        };
        const params = assign(
            isPaginationChange ? {pageNo: nextCurrent, pageSize: nextPageSize} : {pageNo: 1},
            filterData
        );
        updateListParams(params);
        updateFilterData(filterData);
    }, [pageNo, pageSize, updateListParams, updateFilterData]);

    const handlePageNoChange = useCallback(pageNo => {
        updateListParams({pageNo});
    }, [updateListParams]);

    const handlePageSizeChange = useCallback((_pageNo, pageSize) => {
        updateListParams({pageSize});
    }, [updateListParams]);

    const handleFilterChange = useCallback(params => {
        const newParams = {pageNo: 1, ...params};
        updateListParams(newParams);
        updateFilterData(params);
        // 当参数值没有变化时，强制重新加载
        // const needReload = isEqual(listParams, newParams);
        const needReload = every(newParams, (value, key) => isEqual(value, listParams[key]));
        needReload && reload();
    }, [listParams, updateListParams, updateFilterData, reload]);

    const handleSelectedRowKeysSet = useCallback(selectedRowKeys => {
        const selectedItems = map(selectedRowKeys, key => (
            rowKey ? find(dataSource, {[rowKey]: key}) : get(dataSource, key)
        )) as never[];
        setSelectedRow({selectedRowKeys, selectedItems});
    }, [dataSource, rowKey]);

    const handleSelectChange = useCallback((selectedRowKeys: never[]) => {
        handleSelectedRowKeysSet(selectedRowKeys);
        customFunctions?.handleSelectChange?.(selectedRowKeys);
    }, [handleSelectedRowKeysSet, customFunctions]);

    const tableActions = useMemo(() => ({
        loadList,
        loadFirstPage,
        setListParams,
        handleFilterChange,
        // checkAll
        ...customFunctions
    }), [
        loadList,
        loadFirstPage,
        setListParams,
        handleFilterChange,
        customFunctions
    ]);

    Object.assign(callbackRef.current.tableActions, tableActions);
    callbackRef.current.tableOptions = tableOptions;

    const {selectedRowKeys} = selectedRow;

    useEffect(() => {
        // 解决业务侧history back、push等操作时，列表数据不刷新的问题
        if (!isEmpty(queryParamKeys)) {
            let needReload = false;
            forIn(defaultListParams, (paramValue, paramKey) => {
                if (!isEqual(paramValue, listParams[paramKey])) {
                    needReload = true;
                    return false;
                }
            });
            if (!needReload) {
                forIn(listParams, (paramValue, paramKey) => {
                    if (!isEqual(paramValue, defaultListParams[paramKey])) {
                        needReload = true;
                        return false;
                    }
                });
            }
            needReload && setListParams(defaultListParams);
        }
    }, [search]);

    return {
        key: search,
        className,
        title,
        toolbarProps: {
            withToolbar: Boolean(
                multiFilterConfig?.controls?.length
                || controlsConfig?.controls?.length || bulkConfig || bulkComp
                || filterConfig?.controls?.length || filterComp || isMore20
            ),
            controlsConfig,
            multiFilterConfig,
            bulkProps: {
                bulkComp,
                bulkConfig
            },
            filterProps: {
                filterComp,
                filterConfig,
                cloumnsKeyList,
                isMore20,
                setCloumnKeys,
                handleTableCommand
            },
            onSearch: updateListParams,
            onReload: loadList,
            columns
        },
        setColumnsConfig,
        tableProps: {
            rowKey: rowKey || ((__: any, index: number) => index),
            loading,
            dataSource,
            ...(select !== 'none' ? {
                rowSelection: {
                    selectedRowKeys,
                    type: select === 'single' ? 'radio' : 'checkbox',
                    getCheckboxProps: get(tableConfig, 'getCheckboxProps'),
                    onChange: handleSelectChange
                }
            } : {}),
            pagination: {
                current: pageNo,
                pageSize,
                pageSizeOptions: pageSizeOptions || DEFAULT_PAGESIZE_OPTIONS,
                total: totalCount,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: () => `${locale?.total}${totalCount}${locale?.stripData}`,
                ...(card ? {
                    onChange: handlePageNoChange,
                    onShowSizeChange: handlePageSizeChange
                } : {})
            },
            onChange: handleTableChange,
            ...tableConfig,
            columns
        },
        tableComp,
        card,
        emptyConfig,
        dialogProps,
        tableActions,
        filterData,
        listParams,
        httpStatusCode,
        setListParams,
        setSelectedRowKeys: handleSelectedRowKeysSet,
        updateListParams,
        handleCommand,
        handleTableCommand,
        handleFilterChange
    };
};


export default useTable;
